import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RouterModule } from '@angular/router';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { SubscriptionService } from '../../../../core/services/subscription.service';
import { AuthService } from '../../../../core/services/auth.service';
import { NotificationService } from '../../../../core/services/notification.service';
import { EventKitSize, RadicalEvent } from '../../../../core/models/event.model';
import { toBrDateString } from '../../../../core/utils/date.util';
import { describeCancellationPolicy } from '../../../../core/models/cancellation.model';
import { PixPaymentDialogComponent } from '../pix-payment-dialog/pix-payment-dialog.component';

/** Valor enviado ao backend quando o participante não quer kit. */
const NO_KIT = 'SEM_KIT';

@Component({
  selector: 'app-event-subscription-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatDialogModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDividerModule,
    NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
  templateUrl: './event-subscription-dialog.component.html',
  styleUrls: ['./event-subscription-dialog.component.scss']
})
export class EventSubscriptionDialogComponent {
  subscriptionForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private subscriptionService: SubscriptionService,
    private authService: AuthService,
    private notification: NotificationService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<EventSubscriptionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { event: RadicalEvent }
  ) {
    const user = this.authService.currentUserValue;

    let birthDateValue: Date | null = null;
    if (user?.birthDate) {
      const parts = user.birthDate.split('/');
      if (parts.length === 3) {
        birthDateValue = new Date(+parts[2], +parts[1] - 1, +parts[0]);
      }
    }

    this.subscriptionForm = this.fb.group({
      fullName: [user?.name || '', [Validators.required, Validators.minLength(5)]],
      cpf: [user?.cpf || '', [Validators.required, Validators.pattern(/^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/)]],
      birthDate: [birthDateValue, [Validators.required]],
      phone: [user?.phone || '', [Validators.required]],
      emergencyContactName: ['', [Validators.required]],
      emergencyContactPhone: ['', [Validators.required]],
      kitSize: [NO_KIT],
      // Evento pago: aceite obrigatório da política de cancelamento do organizador.
      policyAccepted: [false, this.isPaidEvent ? [Validators.requiredTrue] : []]
    });
  }

  readonly noKit = NO_KIT;

  get eventHasKit(): boolean {
    return (this.data.event.kitSizes?.length ?? 0) > 0;
  }

  get availableKitSizes(): EventKitSize[] {
    return (this.data.event.kitSizes ?? []).filter(kit => kit.availableQuantity > 0);
  }

  get isPaidEvent(): boolean {
    return !!this.data.event.price && this.data.event.price > 0;
  }

  get policySummary(): string {
    const e = this.data.event;
    return e.cancellationPolicySummary
      ?? describeCancellationPolicy(e.cancellationPolicy, e.refundDeadlineDays, e.refundPercent);
  }

  get showServiceFee(): boolean {
    return !!this.data.event.price && this.data.event.price > 0 && !this.data.event.absorbServiceFee;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (this.subscriptionForm.valid) {
      const currentUser = this.authService.currentUserValue;
      if (!currentUser?.id) {
        this.notification.error('Sessão inválida. Faça login novamente para concluir a inscrição.');
        this.dialogRef.close();
        return;
      }

      const normalizedBirthDate = toBrDateString(this.subscriptionForm.value.birthDate);

      const registration = {
        ...this.subscriptionForm.value,
        cpf: (this.subscriptionForm.value.cpf ?? '').toString().replace(/\D/g, ''),
        phone: (this.subscriptionForm.value.phone ?? '').toString().replace(/\D/g, ''),
        emergencyContactPhone: (this.subscriptionForm.value.emergencyContactPhone ?? '').toString().replace(/\D/g, ''),
        birthDate: normalizedBirthDate,
        kitSize: this.eventHasKit ? this.subscriptionForm.value.kitSize : NO_KIT,
        event: { id: this.data.event.id },
        athlete: { id: Number(currentUser.id) }
      };

      this.subscriptionService.subscribe(registration).subscribe({
        next: (newReg) => {
          const hasPrice = this.data.event.price && this.data.event.price > 0;
          if (hasPrice && newReg.id) {
            this.dialogRef.close();
            this.dialog.open(PixPaymentDialogComponent, {
              maxWidth: '550px', width: '95vw',
              data: { registrationId: newReg.id, event: this.data.event },
              panelClass: 'glass-dialog',
              disableClose: true
            });
          } else {
            this.notification.success('Inscrição realizada com sucesso!');
            this.dialogRef.close(true);
          }
        },
        error: (err) => {
          this.notification.error(err.error?.message || 'Erro ao realizar inscrição');
          this.dialogRef.close();
        }
      });
    }
  }
}
